import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Footer from "../../../footer.js";
import Header from "../../../header/header.js";
import Navvbar from "../../../header/Navvbar.js";
import Sidebar from "../../../Sidebar.js";
import './CaseSetting.css';

// Attributes (section currently disabled - imports kept as in original)
import AcrylicColors from './Tabs/attributes/AcrylicColor/AcrylicColors';
import ImplantMarkers from './Tabs/attributes/ImplantMarkers/implantMarkers';
import ImplantSystems from './Tabs/attributes/ImplantSystems/implantsystems';
import CaseMaterials from './Tabs/attributes/MaterialorAlloy/CaseMaterials';
import OrthodonticColors from './Tabs/attributes/OrthodonticColors/OrthodonticColors';
import OrthodonticDesigns from './Tabs/attributes/OrthodonticDesigns/OrthodonticDesigns';
import SpotGurd from './Tabs/attributes/spotgurd/spotgurd';
import SystemAttributes from './Tabs/attributes/SystemAttributes';
import CustomAttributes from './Tabs/attributes/CustomAttributes';

// "Next-case Types" -> "next-case-types"
const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Matches no tab, so all sub-tabs stay closed
const NO_TAB = "__none__";

// General sub-tabs (page components are routed in App.js)
const generalTabs = [
  { key: "Scheduling/Holidays", disabled: true },
  { key: "Internal Remake Types", disabled: true },
  { key: "Next-case Types", disabled: false },
  { key: "Shipping Types", disabled: false },
  { key: "On Hold Types", disabled: true },
  { key: "Canceled Case Reasons", disabled: true },
  { key: "Worktickets", disabled: true },
  { key: "Locations", disabled: true },
].map((t) => ({ ...t, slug: slugify(t.key) }));

// Procedures sub-tabs
const procedureTabs = [
  { key: "Lab Procedures", disabled: false },
  { key: "Display Categories", disabled: false },
  { key: "Tasks", disabled: false },
  { key: "Departments", disabled: false },
  { key: "Components", disabled: true },
  { key: "Enclosures", disabled: true },
  { key: "Specified Files", disabled: true },
  { key: "Revenue Centers", disabled: true },
].map((t) => ({ ...t, slug: slugify(t.key) }));

const CaseSetting = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  // Top section (General / Procedures) is kept in state, NOT in the URL
  const [section, setSection] = useState("General");

  // URL: /caseSetting/<sub-tab-slug>
  const slug = (location.pathname.split("/")[2] || "").toLowerCase();

  const generalMatch = generalTabs.find((t) => t.slug === slug && !t.disabled);
  const procedureMatch = procedureTabs.find((t) => t.slug === slug && !t.disabled);

  // A sub-tab in the URL decides the section; otherwise the clicked section is used
  const activeButton = procedureMatch ? "Procedures" : generalMatch ? "General" : section;
  const activeGeneralTab = generalMatch ? generalMatch.key : NO_TAB;
  const activeProcedureTab = procedureMatch ? procedureMatch.key : NO_TAB;

  // If another page navigates here with state, convert it to a URL
  useEffect(() => {
    if (location.state?.activeSection && location.state?.activeTab) {
      // Attributes section is disabled
      if (location.state.activeSection !== "Attributes") {
        navigate(`/caseSetting/${slugify(location.state.activeTab)}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Error boundary effect to catch errors from child components
  useEffect(() => {
    const handleError = () => {
      setHasError(true);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Top buttons: URL stays /caseSetting, sub-tabs are closed
  const handleButtonClick = (buttonName) => {
    if (buttonName !== "Attributes") {
      setSection(buttonName);
      navigate("/caseSetting");
    }
  };

  if (hasError) {
    return (
      <>
        <div className="cs-error-header">
          <Header />
          <Navvbar />
        </div>
        <div className="cs-error-container">
          <div className="alert alert-danger" role="alert">
            <h4>Something went wrong</h4>
            <p>There was an error loading the component. Please try refreshing the page.</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="cs-header-container">
        <Header />
        <Navvbar />
      </div>
      <div className="cs-main-wrapper">
        <div className="row cs-row">
          <div className="col-md-2 cs-sidebar">
            <Sidebar />
          </div>
          <div className="col-md-10 cs-content">
            <h1 className="cs-content-title">Case Settings</h1>
            <div className="cs-tabs-title">
              <button className={`cs-tab-btn ${activeButton === "General" ? "cs-tab-active" : ""}`} onClick={() => handleButtonClick("General")}>General</button>
              <span className="cs-tab-gap"></span>
              <button className={`cs-tab-btn ${activeButton === "Procedures" ? "cs-tab-active" : ""}`} onClick={() => handleButtonClick("Procedures")}>Procedures</button>
              <span className="cs-tab-gap"></span>
              <button className="cs-tab-btn cs-tab-disabled" onClick={() => handleButtonClick("Attributes")} disabled title="Attributes tab is currently disabled">Attributes</button>
            </div>

            <div className="cs-tabs-container">
              {activeButton === "General" && (
                <Tabs
                  activeKey={activeGeneralTab}
                  onSelect={(k) => {
                    const tab = generalTabs.find((t) => t.key === k);
                    if (tab && !tab.disabled) {
                      navigate(`/caseSetting/${tab.slug}`);
                    }
                  }}
                  id="general-tabs"
                  className="mb-3"
                >
                  {generalTabs.map((tab) => (
                    <Tab key={tab.key} eventKey={tab.key} title={tab.key} disabled={tab.disabled} />
                  ))}
                </Tabs>
              )}
              {activeButton === "Procedures" && (
                <Tabs
                  activeKey={activeProcedureTab}
                  onSelect={(k) => {
                    const tab = procedureTabs.find((t) => t.key === k);
                    if (tab && !tab.disabled) {
                      navigate(`/caseSetting/${tab.slug}`);
                    }
                  }}
                  id="procedures-tabs"
                  className="mb-3"
                >
                  {procedureTabs.map((tab) => (
                    <Tab key={tab.key} eventKey={tab.key} title={tab.key} disabled={tab.disabled} />
                  ))}
                </Tabs>
              )}

              {/* Active tab page is rendered here (routes defined in App.js) */}
              <div className="tab-content-wrapper">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="cs-footer-wrapper">
        <div className="cs-footer-inner"><Footer /></div>
      </div>
    </>
  );
};

export default CaseSetting;